import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { BitmapText, Container, Graphics, Text } from "pixi.js";
import { Tween } from "tweedle.js";
import { isSuperQuickBtnOnGlobal, languageGlobal } from "../../config/GlobalVariableConfig";
import { getFontConfig } from "../../config/introTextConfig";
import { DisplayObject, IDisplayObject } from "../../core/displays/DisplayObject";
import {
    createUpgradeTitleStyle,
    JACKPOT_UPGRADE_AMOUNT_FONT_SIZE,
    JACKPOT_UPGRADE_TITLE_FONT_SIZE
} from "../../core/jackpotUpgradeText";
import { wait } from "../../core/utils";
import { GameConstants } from "../../gameClientEvents/GameConstants";
import { sound } from "@pixi/sound";
import { SoundConstants } from "../../gameClientEvents/SoundConstants";

declare const window: any;
const translationManager = window.hacksawCasino.Translation;

// const HEADING_TRANSLATION_KEY = translationManager.game('bonus_game_completed');
// const HEADING_FALLBACK =  "BONUS GAME COMPLETED!";

// const JACKPOT_WON_TRANSLATION_KEY = translationManager.game('jackpot_won');
// const JACKPOT_WON_FALLBACK = "JACKPOT WON";

const BASE_METER_OVERLAY_FONT_SIZE = 28;
const BASE_METER_SCALE = 1.5;
type LayoutConfig = {
    node: Container;
    x?: number;
    y?: number;
    scale?: number;
};
export class JackpotWinBanner extends DisplayObject {
    private readonly BASE_METER_SKELETON = "jackpotBaseMeterSkel";
    private readonly BASE_METER_ATLAS = "jackpotBaseMeterAtlas";

    private blackLayer: Graphics = new Graphics();

    private contentContainer!: Container;
    private headingText!: Text;
    private baseMeterSpine!: Spine;
    private baseMeterOverlayText!: Text;
    private jackpotValueText!: Text;
    private winAmountText!: BitmapText;

    private titleFontSize = JACKPOT_UPGRADE_TITLE_FONT_SIZE;
    private amountFontSize = JACKPOT_UPGRADE_AMOUNT_FONT_SIZE;

    constructor(config: IDisplayObject) {
        super(config);
        this.init();
        this.visible = false;
    }

    private init(): void {
        this.setupOverlay();
        this.setupContent();
    }

    private setupOverlay(): void {
        this.blackLayer
            .rect(-10000, -10000, 20000, 20000)
            .fill(0x000000);

        this.blackLayer.alpha = 0.65;
        this.blackLayer.visible = false;

        this.addChild(this.blackLayer);
    }

    private setupContent(): void {
        const {
            fontFamily: fontFamilyHeading,
            fontWeight: fontWeightHeading,
        } = getFontConfig(languageGlobal, "jackpotHeading");

        this.contentContainer = new Container();
        this.contentContainer.label = "jackpotWinBannerContent";
        this.contentContainer.position.set(
            GameConstants.BACKGROUND_SIZE / 2,
            GameConstants.BACKGROUND_SIZE / 2
        );
        this.contentContainer.visible = false;

        this.addChild(this.contentContainer);

        this.headingText = new Text({
            text: "",
            style: createUpgradeTitleStyle(
                fontFamilyHeading,
                fontWeightHeading,
                this.titleFontSize
            ),
        });
        this.headingText.anchor.set(0.5);

        this.baseMeterSpine = Spine.from({
            skeleton: this.BASE_METER_SKELETON,
            atlas: this.BASE_METER_ATLAS,
        });
        this.baseMeterSpine.label = "jackpotWinBannerBaseMeter";
        this.baseMeterSpine.scale.set(BASE_METER_SCALE);
        this.baseMeterSpine.state.setAnimation(0, "idle", true);

        this.baseMeterOverlayText = new Text({
            text: "",
            style: createUpgradeTitleStyle(
                fontFamilyHeading,
                fontWeightHeading,
                BASE_METER_OVERLAY_FONT_SIZE
            ),
        });
        this.baseMeterOverlayText.anchor.set(0.5);
        this.baseMeterOverlayText.label = "baseMeterOverlayText";
        this.baseMeterSpine.addChild(this.baseMeterOverlayText);

        this.jackpotValueText = new Text({
            text: "",
            style: createUpgradeTitleStyle(
                fontFamilyHeading,
                fontWeightHeading,
                this.titleFontSize
            ),
        });
        this.jackpotValueText.anchor.set(0.5);

        this.winAmountText = new BitmapText({
            text: "",
            style: {
                fontFamily: "scarab_wins_digit",
                fontSize: this.amountFontSize,
                align: "center",
            },
        });
        this.winAmountText.anchor.set(0.5);

        this.contentContainer.addChild(
            this.headingText,
            this.baseMeterSpine,
            this.jackpotValueText,
            this.winAmountText
        );
    }

    // private getHeadingText(): string {
    //     return this.lookupTranslation(
    //         HEADING_TRANSLATION_KEY,
    //         HEADING_FALLBACK
    //     );
    // }

    // private getJackpotWonLabel(): string {
    //     return this.lookupTranslation(
    //         JACKPOT_WON_TRANSLATION_KEY,
    //         JACKPOT_WON_FALLBACK
    //     );
    // }

    // private lookupTranslation(key: string, fallback: string): string {
    //     try {
    //         const translated = translationManager?.game?.(key, null);

    //         if (
    //             typeof translated === "string"
    //             && translated.length > 0
    //             && translated !== key
    //             && translated !== `{${key}}`
    //         ) {
    //             return translated;
    //         }
    //     } catch {
    //         // ignored
    //     }

    //     return fallback;
    // }

    private layoutContent(): void {

        const layout: LayoutConfig[] = [

            {
                node: this.headingText,
                x: 0,
                y: -260,
                scale: 1,
            },

            {
                node: this.baseMeterSpine,
                x: 0,
                y: -40,
                scale: 1.5,
            },

            {
                node: this.jackpotValueText,
                x: 0,
                y: 160,
                scale: 1,
            },

            {
                node: this.winAmountText,
                x: 0,
                y: 350,
                scale: 1,
            },
        ];

        layout.forEach((item) => {

            item.node.position.set(
                item.x ?? 0,
                item.y ?? 0
            );

            item.node.scale.set(
                item.scale ?? 1
            );
        });
    }

    public play(
        jackpotMultiplier: number,
        winAmount: number,
        callback?: () => void
    ): Promise<void> {
        return new Promise<void>((resolve) => {
            const hasJackpot = jackpotMultiplier > 0;

            this.headingText.text = translationManager.game('bonus_game_completed');

            this.baseMeterOverlayText.text = hasJackpot
                ? translationManager.game('current_jackpot') + ` ${jackpotMultiplier}x`
                : "";
            this.baseMeterOverlayText.visible = hasJackpot;

            this.jackpotValueText.text = translationManager.game('jackpot_won');
            this.jackpotValueText.visible = true;

            this.winAmountText.text = winAmount.toFixed(2);
            this.winAmountText.visible = true;

            this.layoutContent();

            const dustAnim = Spine.from({
                skeleton: "bannerDustSkel",
                atlas: "bannerDustAtlas",
            });
            dustAnim.label = "jackpotWinBannerDust";
            dustAnim.position.set(
                GameConstants.BACKGROUND_SIZE / 2 - 275,
                GameConstants.BACKGROUND_SIZE / 2
            );
            this.addChild(dustAnim);
            dustAnim.state.setAnimation(0, "in", false);
            dustAnim.state.addAnimation(0, "loop", true, 0);

            this.baseMeterSpine.state.setAnimation(0, "win", false);
            this.baseMeterSpine.state.addAnimation(0, "idle", true, 0);

            this.visible = true;
            this.blackLayer.visible = true;
            this.contentContainer.visible = true;

            const targetScale = Math.min(1500 / this.contentContainer.width, 1);

            this.contentContainer.scale.set(0.01);
            sound.play(SoundConstants.JACKPOT_UPGRADED, { volume: 0.8 });
            new Tween(this.contentContainer.scale)
                .to({ x: targetScale, y: targetScale }, 200)
                .start()
                .onComplete(async () => {
                    await (isSuperQuickBtnOnGlobal ? wait(1000) : wait(2500));

                    dustAnim.state.setAnimation(0, "out", false);
                    dustAnim.state.addListener({
                        complete: () => {
                            dustAnim.destroy({ children: true });
                            this.visible = false;
                        },
                    });

                    new Tween(this.contentContainer.scale)
                        .to({ x: 0, y: 0 }, 200)
                        .start()
                        .onComplete(() => {
                            this.blackLayer.visible = false;
                            this.contentContainer.visible = false;
                            // this.visible = false;
                            this.contentContainer.scale.set(1);
                            callback?.();
                            resolve();
                        });
                });
        });
    }
}
